import { trpc } from "@/api/client";
import { SelectOption } from "@/components/core-components/organisms/Dropdown/components/Option/option.index";
import dispatchToast from "@/utils/dispatch-toast";
import { useSelectedCompany } from "@flash-tecnologia/hros-web-utility";
import { useEffect, useMemo, useState } from "react";
import { REQUIRED_FORM_FIELD_ERROR } from "../constants";
import { PermissionProfiles, PermissionRole, RoleBuiltInIds } from "../types";

export function usePermissionsDrawer({
  defaultPermissionProfile,
  defaultPermissionRoles,
  onSubmit,
}: {
  defaultPermissionProfile?: PermissionProfiles;
  defaultPermissionRoles?: PermissionRole[];
  onSubmit: (
    permissionProfile: PermissionProfiles,
    permissionRoles?: PermissionRole[]
  ) => void;
}) {
  const { selectedCompany } = useSelectedCompany();

  const [selectedPermissionProfile, setSelectedPermissionProfile] = useState<
    PermissionProfiles | undefined
  >(defaultPermissionProfile);

  const [selectedPermissionRoles, setSelectedPermissionRoles] = useState<
    PermissionRole[]
  >(defaultPermissionRoles ?? []);

  const [allPermissionRolesSelected, setAllPermissionRolesSelected] =
    useState(false);

  const [permissionRolesError, setPermissionRolesError] = useState<string>();

  const { data: permissionRoles } = trpc.getPermissionRoles.useQuery(
    {
      companyId: selectedCompany.id,
      builtInIds: [
        RoleBuiltInIds.ADMIN,
        RoleBuiltInIds.BENEFIT_ADMIN,
        RoleBuiltInIds.EXPENSE_ADMIN,
      ],
    },
    { enabled: !!selectedCompany.id }
  );

  const handleAllPermissionRolesChange = (checked: boolean) => {
    setAllPermissionRolesSelected(checked);
    if (checked) {
      setSelectedPermissionRoles(
        permissionRolesOptions.map(({ value }) => value)
      );
      setPermissionRolesError(undefined);
    } else {
      setSelectedPermissionRoles([]);
    }
  };

  const handlePermissionProfileChange = (
    permissionProfile: PermissionProfiles
  ) => {
    setSelectedPermissionProfile(permissionProfile);
    setSelectedPermissionRoles(defaultPermissionRoles ?? []);
    setAllPermissionRolesSelected(
      defaultPermissionRoles?.length === permissionRolesOptions?.length
    );
  };

  const handlePermissionRolesBlur = () => {
    if (!selectedPermissionRoles.length) {
      setPermissionRolesError(REQUIRED_FORM_FIELD_ERROR);
    } else {
      setPermissionRolesError(undefined);
    }
  };

  const handlePermissionRolesChange = (
    checked: boolean,
    option: SelectOption
  ) => {
    let updatedSelectedPermissionRoles: PermissionRole[] = [];
    if (checked) {
      updatedSelectedPermissionRoles = selectedPermissionRoles.concat(
        option.value
      );
      setPermissionRolesError(undefined);
    } else {
      updatedSelectedPermissionRoles = selectedPermissionRoles.filter(
        (module) => module !== option.value
      );
    }
    setSelectedPermissionRoles(updatedSelectedPermissionRoles);
    setAllPermissionRolesSelected(
      updatedSelectedPermissionRoles.length === permissionRolesOptions?.length
    );
  };

  const handleSubmitClick = () => {
    if (!selectedPermissionProfile) {
      dispatchToast({
        content: "Escolha um nível de permissionamento para continuar.",
        type: "error",
      });
      return;
    }

    if (
      selectedPermissionProfile === PermissionProfiles.ADMINISTRATOR &&
      !selectedPermissionRoles?.length
    ) {
      setPermissionRolesError(REQUIRED_FORM_FIELD_ERROR);
      return;
    }

    if (selectedPermissionProfile === PermissionProfiles.ADMINISTRATOR) {
      onSubmit(selectedPermissionProfile, selectedPermissionRoles);
    } else {
      onSubmit(selectedPermissionProfile);
    }

    resetForm();
  };

  const resetForm = () => {
    setSelectedPermissionProfile(defaultPermissionProfile);
    setSelectedPermissionRoles(defaultPermissionRoles ?? []);
    setAllPermissionRolesSelected(false);
    setPermissionRolesError(undefined);
  };

  const permissionRolesOptions: SelectOption[] = useMemo(
    () =>
      permissionRoles
        ? permissionRoles.map((permissionRole) => ({
            id: permissionRole.id,
            label: permissionRole.name,
            value: permissionRole,
          }))
        : [],
    [permissionRoles]
  );

  useEffect(() => {
    setSelectedPermissionProfile(defaultPermissionProfile);
    setSelectedPermissionRoles(defaultPermissionRoles ?? []);
    setAllPermissionRolesSelected(
      defaultPermissionRoles?.length === permissionRolesOptions?.length
    );
    setPermissionRolesError(undefined);
  }, [
    defaultPermissionProfile,
    defaultPermissionRoles,
    permissionRolesOptions,
  ]);

  return {
    allPermissionRolesSelected,
    permissionRolesError,
    permissionRolesOptions,
    selectedPermissionProfile,
    selectedPermissionRoles,
    onAllPermissionRolesChange: handleAllPermissionRolesChange,
    onPermissionProfileChange: handlePermissionProfileChange,
    onPermissionRolesBlur: handlePermissionRolesBlur,
    onPermissionRolesChange: handlePermissionRolesChange,
    onSubmitClick: handleSubmitClick,
    resetForm,
  };
}
